import React, { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { states, State } from '@/data/states';

interface StateComboboxProps {
    selectedValue?: string | null;
    onChange: (value: string | null) => void;
    disabled?: boolean;
    error?: string;
    'data-testid'?: string;
}

const StateCombobox: React.FC<StateComboboxProps> = ({ selectedValue, onChange, disabled, error, 'data-testid': testId }) => {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<State | null>(null);

    useEffect(() => {
        const found = states.find(item => item.id === selectedValue);
        setSelected(found || null);
    }, [selectedValue]);

    const filteredItems =
        query === ''
            ? states
            : states.filter(item =>
                item.name.toLowerCase().includes(query.toLowerCase()) ||
                item.id.toLowerCase().includes(query.toLowerCase())
            );

    const handleSelectionChange = (item: State | null) => {
        setSelected(item);
        onChange(item ? item.id : null);
    };

    return (
        <Combobox as="div" value={selected} onChange={handleSelectionChange} disabled={disabled} className="mt-1">
            <div className="relative">
                <Combobox.Input
                    className={`input-primary w-full ${error ? 'border-red-500' : ''}`}
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(item: State | null) => item?.id || ''}
                    placeholder="UF"
                    autoComplete="off"
                    data-testid={testId}
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>

                {filteredItems.length > 0 && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredItems.map((item) => (
                            <Combobox.Option
                                key={item.id}
                                value={item}
                                className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                            >
                                {({ active, selected }) => (
                                    <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : ''}`}>
                                            {item.name} ({item.id})
                                        </span>
                                        {selected && (
                                            <span
                                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}
                                            >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                )}
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </Combobox>
    );
};

export default StateCombobox; 